import {
   contains,
   each,
   extend,
   existy,
   notExisty,
   get,
   head,
   find,
   isNotEmpty,
   upperFirst,
   addProperty,
   absolutizeUrl,
   createElement
} from '../utils';

import { methods, properties } from '../html5media';
import { VIDEO, HLS, AUDIO } from './plugins';

const renderers = [VIDEO, HLS, AUDIO];

const ELEMENT_NODE = 1;

let idx = 0;

/**
 * Create a fake DOM element with properties that look like a real HTMLMediaElement
 * with all its methods/properties/events.
 *
 * @constructor
 * @param {HTMLElement} node element
 * @param {Object} options some options
 * @return {HTMLElement} created element
 */
export default function MediaElement(node, options = {}) {
   // create our node (note: older versions of iOS don't support Object.defineProperty on DOM nodes)
   var mediaElement = createElement('div', { class: 'rmp-flash-wrapper' });

   mediaElement.options = options;

   mediaElement.originalNode = node;
   const id = node.id || 'me_flash_' + ++idx;

   // change id
   mediaElement.originalNode.setAttribute('id', id + '_from_mejs');

   // add next to this one
   if (mediaElement.originalNode.parentNode) {
      mediaElement.originalNode.parentNode.insertBefore(mediaElement, mediaElement.originalNode);
   }

   // insert this one inside
   mediaElement.appendChild(mediaElement.originalNode);

   mediaElement.id = id;

   mediaElement.renderers = {};
   mediaElement.renderer = null;
   mediaElement.rendererName = null;

   // add properties get/set
   each(properties, createAssigner(mediaElement));

   // special .src property
   const getSrc = () => existy(mediaElement.renderer) ? mediaElement.renderer.getSrc() : null;
   const setSrc = value => {
      let renderInfo;
      const mediaFiles = [];

      // clean up URLs
      if (typeof value === 'string') {
         mediaFiles.push({
            src: value,
            type: value ? getTypeFromFile(value) : ''
         });
      } else {
         each(value, data => {
            const src = absolutizeUrl(data.src), type = data.type;
            mediaFiles.push({
               src,
               type: type || getTypeFromFile(src)
            });
         });
      }

      mediaElement.originalNode.setAttribute('src', get(head(mediaFiles), 'src', ''));

      // find a renderer and URL match
      renderInfo = selectRenderer(mediaFiles);

      // Ensure that the original gets the first source found
      mediaElement.originalNode.setAttribute('src', get(head(mediaFiles), 'src', ''));

      // did we find a renderer?
      if (renderInfo === null) {
         options.bus.emit('error', { message: 'No renderer found'} );
         mediaElement.options.logger.error('No renderer found');
         return;
      }

      // turn on the renderer (this checks for the existing renderer already)
      mediaElement.changeRenderer(renderInfo.rendererName, mediaFiles);

      if (notExisty(mediaElement.renderer)) {
         options.bus.emit('error', { message: 'Error creating renderer'} );
         mediaElement.options.logger.error('Error creating renderer');
      }
   };

   addProperty(mediaElement, 'src', getSrc, setSrc);
   mediaElement.getSrc = getSrc;
   mediaElement.setSrc = setSrc;

   each(methods, createMethodAssigner(mediaElement));

   mediaElement.changeRenderer = createChanger(mediaElement);

   mediaElement.setSize = (width, height) => {
      if (existy(mediaElement.renderer)) {
         mediaElement.renderer.setSize(width, height);
      } else {
         mediaElement.options.logger.warn('Could not set the sizes. Renderer is not found');
      }
   };

   // find <source> elements
   if (existy(mediaElement.originalNode)) {
      detectSources(mediaElement);
   }

   return mediaElement;
}

function createChanger(mediaElement) {
   return function changeRenderer(rendererName, mediaFiles) {

      // check for a match on the current renderer
      if (get(mediaElement, 'renderer.name') === rendererName) {
         mediaElement.options.logger.debug('Already using: ', rendererName);

         mediaElement.renderer.show();
         mediaElement.renderer.setSrc(head(mediaFiles).src);

         return true;
      }

      // if existing renderer is not the right one, then hide it
      if (existy(mediaElement.renderer)) {
         mediaElement.options.logger.debug('Stopping and hiding: ', mediaElement.renderer);
         mediaElement.renderer.pause();
         if (mediaElement.renderer.stop) {
            mediaElement.renderer.stop();
         }
         mediaElement.renderer.hide();
      }

      // see if we have the renderer already created
      let newRenderer = mediaElement.renderers[rendererName];

      if (existy(newRenderer)) {
         mediaElement.options.logger.debug('restoring: ', newRenderer.name);

         newRenderer.show();
         newRenderer.setSrc(head(mediaFiles).src);

         mediaElement.renderer = newRenderer;
         return true;
      }

      const renderer = find(renderers, r => r.name === rendererName);

      if (notExisty(renderer)) {
         mediaElement.options.logger.error('can\'t found: ' + rendererName);
         return false;

      }

      var renderOptions = extend({}, mediaElement.options, renderer.options);
      newRenderer = renderer.create(mediaElement, renderOptions, mediaFiles);
      newRenderer.name = rendererName;

      // store for later
      mediaElement.renderers[renderer.name] = newRenderer;
      mediaElement.renderer = newRenderer;
      mediaElement.rendererName = rendererName;
      newRenderer.show();

      return true;
   };
}

function createAssigner(mediaElement) {
   return function assignGettersSetters(propName) {
      // src is a special one below
      if (propName === 'src') {
         return;
      }

      const capName = upperFirst(propName),
         getFn = () => {
            if (existy(mediaElement.renderer)) {
               return mediaElement.renderer['get' + capName]();
            }
            mediaElement.options.logger.warn(`Could not called getter "${capName}". Renderer is empty`);
            return null;
         },
         setFn = (value) => {
            if (existy(mediaElement.renderer)) {
               mediaElement.renderer['set' + capName](value);
            } else {
               mediaElement.options.logger.warn(`Could not called setter "${capName}". Renderer is empty`);
            }
         };

      addProperty(mediaElement, propName, getFn, setFn);

      mediaElement['get' + capName] = getFn;
      mediaElement['set' + capName] = setFn;
   };
}

function createMethodAssigner(mediaElement) {
   return function methodAssigner(methodName) {
      // run the method on the current renderer
      mediaElement[methodName] = (...args) => {
         mediaElement.options.logger.debug('[wrapper ', mediaElement.id, '.', methodName, '()]', mediaElement.renderer);
         if (get(mediaElement, `renderer.${methodName}`)) {
            return mediaElement.renderer[methodName](args);
         }
         mediaElement.options.logger.warn(`Could not called method "${methodName}"`);
         return null;
      };
   };
}

function detectSources(mediaElement) {
   const mediaFiles = [];
   const node = mediaElement.originalNode;

   switch (node.nodeName.toLowerCase()) {

   case 'iframe':
      mediaFiles.push({ type: '', src: node.getAttribute('src') });

      break;

   case 'audio':
   case 'video':
      var
         sources = node.childNodes.length,
         nodeSource = node.getAttribute('src');

      // Consider if node contains the `src` and `type` attributes
      if (nodeSource) {
         mediaFiles.push({ src: nodeSource, type: formatType(nodeSource, node.getAttribute('type')) });
      }

      // test <source> types to see if they are usable

      for (let i = 0; i < sources; i++) {
         const n = node.childNodes[i];
         if (n.nodeType === ELEMENT_NODE && n.tagName.toLowerCase() === 'source') {
            const src = n.getAttribute('src');
            mediaFiles.push({ src, type: formatType(src, n.getAttribute('type')) });
         }
      }
      break;
   }

   if (isNotEmpty(mediaFiles)) {
      mediaElement.options.logger.debug('initializing src', head(mediaFiles).src);
      mediaElement.src = mediaFiles;
   }
}

// Return the mime part of the type in case the attribute contains the codec
// (`video/mp4; codecs="avc1.42E01E, mp4a.40.2"` becomes `video/mp4`)
function getMimeFromType(type) {
   // eslint-disable-next-line no-bitwise
   if (type && ~type.indexOf(';')) {
      return head(type.split(';'));
   }
   return type;
}

// Get the format of a specific media, either based on URL or its mime type
function formatType(url, type) {
   // if no type is supplied, fake it with the extension
   if (url && !type) {
      return getTypeFromFile(url);
   }
   return getMimeFromType(type);
}

// Get the type of media based on URL structure
function getTypeFromFile(url) {

   url = url.toLowerCase();

   if (contains(url, 'rtmp')) {
      if (contains(url, '.mp3')) {
         return 'audio/rtmp';
      }
      return 'video/rtmp';
   } else if (contains(url, '.oga') || contains(url, '.ogg')) {
      return 'audio/ogg';
   } else if (contains(url, '.m3u8') || contains(url, 'm3u8-aapl-v3')) {
      return 'application/x-mpegURL';
   } else if (contains(url, '.mpd')) {
      return 'application/dash+xml';
   }

   // the do standard extension check
   const ext = getExtension(url), normalizedExt = normalizeExtension(ext);
   return (/(mp4|m4v|ogg|ogv|webm|webmv|flv|wmv|mpeg|mov)/gi.test(ext) ? 'video' : 'audio') + '/' + normalizedExt;
}

function getExtension(url) {
   const pure = head(url.split('?'));
   // eslint-disable-next-line no-bitwise
   if (~pure.indexOf('.')) {
      let idx = pure.lastIndexOf('.');
      return pure.substring(++idx);
   }
   return '';
}

//  Get standard extension of a media file
function normalizeExtension(extension) {
   switch (extension) {
   case 'mp4':
   case 'm4v':
      return 'mp4';
   case 'webm':
   case 'webma':
   case 'webmv':
      return 'webm';
   case 'ogg':
   case 'oga':
   case 'ogv':
      return 'ogg';
   default:
      return extension;
   }
}

function selectRenderer(mediaFiles) {
   for (let i = 0, il = renderers.length; i < il; i++) {
      const renderer = renderers[i];
      const res = renderer4files(renderer, mediaFiles);
      if (existy(res)) {
         return res;
      }
   }
   return null;
}

function renderer4files(renderer, mediaFiles) {
   const file = find(mediaFiles, file => renderer.canPlayType(file.type));
   return existy(file) ? { rendererName: renderer.name, src: file.src } : null;
}
